import { Link } from 'react-router-dom';
import { MessageCircle, Check, ArrowUpRight, Scissors, Timer, Sparkles } from 'lucide-react';
import Reveal from '@/components/Reveal';
import Seo from '@/components/Seo';
import Faq from '@/components/Faq';
import GoogleReviews from '@/components/GoogleReviews';
import { CONTACT, buildWhatsappLink } from '@/constants/site';

const OFFER_MSG =
  'I found this offer on the website, I want to claim the offer — 3 suits for AED 3333.';
const WA_LINK = buildWhatsappLink(OFFER_MSG);

const KEYWORDS = [
  'bespoke tailoring service in al karama dubai',
  'custom suits tailor in al karama dubai',
  'made to measure suits tailor in al karama dubai',
  'custom made suits tailor in al karama dubai',
  'personalized tailoring services in al karama dubai',
  'custom suits couturier in karama dubai',
  'tailored suits in al karama dubai',
  'handmade suits tailor in al karama dubai',
  'best suit tailor in al karama dubai',
  'luxury tailor in al karama dubai',
  'luxury tailoring company in al karama dubai',
  "luxury men's outfit tailor in al karama dubai",
  'bombay bespoke tailor in al karama dubai',
  'bespoke tailoring services in al karama dubai',
  'tailored business suits in al karama dubai',
  "men's bespoke suits al karama dubai",
  "men's suits tailor in al karama dubai",
  'formal wear tailor in al karama dubai',
  'gents tailor in al karama dubai',
  'wedding suit tailor in al karama dubai',
  'wedding suits bespoke designer in karama',
  'affordable custom suits tailor in al karama dubai',
  'suit alterations tailor in al karama dubai',
  'outfit alteration services in al karama dubai',
];

const SALE_FAQS = [
  {
    q: 'What is included in the AED 3333 offer?',
    a: 'The end-of-season offer includes three fully bespoke men’s suits — cloth, pattern drafting, up to two fittings and finishing. Choose your preferred fabric weights and lining from our curated selection, plus you receive complimentary consultation with Mr. Hardeep Singh Gill at our Al Karama atelier.',
  },
  {
    q: 'Is the offer valid for women as well?',
    a: 'Yes — women’s bespoke suits, blazers and pantsuits are also available in the offer through VM by Vandana. Please mention this when you WhatsApp us and we will design your fitting accordingly.',
  },
  {
    q: 'How long does the tailoring take?',
    a: 'Express turnaround for this offer — three bespoke suits delivered within 3 to 5 days from your first fitting, including one baste fitting and one forward fitting. Message us on WhatsApp for your exact delivery date.',
  },
  {
    q: 'Do you offer suit alterations as well?',
    a: 'Yes, we accept alterations for existing garments (jackets, trousers, shirts, dresses) at our Al Karama atelier. Alteration services are separate from the AED 3333 bespoke suit offer.',
  },
  {
    q: 'Where do I claim the offer?',
    a: 'Tap any \u201cWhatsApp Now\u201d button on this page to message our atelier directly at +971 54 279 1122, or visit us at Arif and Bintoak Building, Ground Floor, Shop 8, Al Karama, Dubai.',
  },
  {
    q: 'Do you also make wedding suits and sherwanis?',
    a: 'Absolutely. As a leading wedding suit tailor in Al Karama Dubai, we make bespoke tuxedos, three-piece suits, sherwanis, achkans and Indo-Western wedding wear for grooms and groomsmen.',
  },
];

// Small pill button — gold WhatsApp CTA reused throughout the page
const ClaimBtn = ({ label = 'WhatsApp now to claim', big = false, source = 'sale-page' }) => (
  <a
    href={WA_LINK}
    target="_blank"
    rel="noreferrer"
    onClick={() => window.gtag && window.gtag('event', 'Whatsapp', { source })}
    className={`inline-flex items-center gap-3 ${big ? 'px-8 py-4 text-[12px]' : 'px-6 py-3.5 text-[11px]'} bg-[#25D366] text-white uppercase tracking-[0.24em] font-semibold hover:bg-[#1eb958] transition-colors duration-300`}
    data-testid="sale-whatsapp-cta"
  >
    <MessageCircle size={big ? 18 : 15} />
    {label}
  </a>
);

export default function Sale() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Offer',
    name: 'End of Season — 3 Bespoke Men Suits for AED 3333',
    description:
      'Bombay Bespoke Fashion House end-of-season offer — three bespoke men’s suits, made to measure at our Al Karama, Dubai atelier, for AED 3333.',
    price: '3333',
    priceCurrency: 'AED',
    availability: 'https://schema.org/InStock',
    seller: {
      '@type': 'ClothingStore',
      name: 'Bombay Bespoke Fashion House LLC',
      telephone: '+971542791122',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Arif and Bintoak Building, Ground Floor, Shop 8',
        addressLocality: 'Al Karama',
        addressRegion: 'Dubai',
        addressCountry: 'AE',
      },
    },
    url: 'https://bombaybespokefashion.com/sale',
    validFrom: new Date().toISOString().slice(0, 10),
  };

  return (
    <div className="bg-[hsl(var(--bb-ivory))]" data-testid="sale-page">
      <Seo
        title="Sale — 3 Bespoke Suits for AED 3333 | Bombay Bespoke Fashion House, Al Karama Dubai"
        description="End-of-season offer: get 3 bespoke men suits for AED 3333, made to measure by Mr. Hardeep Singh Gill at our Al Karama, Dubai atelier. Custom, tailored and luxury bespoke tailoring services for men and women."
        canonical="https://bombaybespokefashion.com/sale"
        ogImage="https://bombaybespokefashion.com/assets/promo-suits.jpg"
        jsonLd={jsonLd}
      />

      {/* HERO — banner + headline + CTA */}
      <section className="relative bg-[hsl(var(--bb-charcoal))] text-[hsl(var(--bb-text))] overflow-hidden bb-grain">
        <div className="relative z-10 bb-container py-14 md:py-24 grid grid-cols-12 gap-10 items-center">
          <div className="col-span-12 lg:col-span-6">
            <Reveal>
              <div className="bb-eyebrow text-[hsl(var(--bb-gold))]">End of Season Offer · Al Karama, Dubai</div>
            </Reveal>
            <Reveal delay={120}>
              <h1 className="bb-display text-[54px] sm:text-[72px] lg:text-[96px] mt-4 leading-[0.94]">
                Get 3 Men Suits
                <br />
                <span className="bb-italic text-[hsl(var(--bb-gold))]">for AED 3333.</span>
              </h1>
            </Reveal>
            <Reveal delay={240}>
              <p className="mt-7 max-w-xl text-base md:text-lg text-[hsl(var(--bb-text))]/72 leading-relaxed">
                Custom bespoke suits for men <span className="text-[hsl(var(--bb-gold))]">and women</span>, made to
                measure at our Al Karama atelier by master tailor Mr. Hardeep Singh Gill. Three decades of craftsmanship,
                three suits, one price.
              </p>
            </Reveal>
            <Reveal delay={340}>
              <div className="mt-10 flex flex-wrap items-center gap-5">
                <ClaimBtn big label="WhatsApp now — 054 279 1122" source="sale-hero" />
                <a
                  href={`tel:${CONTACT.phoneRaw}`}
                  className="bb-link bb-link-reverse text-[hsl(var(--bb-gold))]"
                >
                  Or call the atelier →
                </a>
              </div>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-6">
            <Reveal className="bb-image-hover relative aspect-[3/2] overflow-hidden border border-[hsl(var(--bb-text))]/10">
              <picture>
                <source srcSet="/assets/promo-suits.webp" type="image/webp" />
                <img
                  src="/assets/promo-suits.jpg"
                  alt="End of Season Offer — 3 Men Suits for AED 3333 at Bombay Bespoke, Al Karama Dubai"
                  className="w-full h-full object-cover"
                />
              </picture>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED — 3 pillars */}
      <section className="bb-container py-20 md:py-28">
        <div className="grid grid-cols-12 gap-6 md:gap-10">
          {[
            { icon: <Scissors size={22} />, title: '3 Bespoke Suits',
              body: 'Three fully custom men suits — jacket, trousers and optional waistcoat. Choose your fabric weight, colour and lining from our curated cloth library.' },
            { icon: <Sparkles size={22} />, title: 'Master Tailored',
              body: 'Every pattern drafted by Mr. Gill in person. Two personal fittings ensure the drape sits like a second skin. Hand-finished lapels and buttonholes.' },
            { icon: <Timer size={22} />, title: '3 to 5 Days Delivery',
              body: 'Express turnaround for the offer — three bespoke suits delivered within 3 to 5 days of your first fitting. Complimentary alterations for the first 30 days.' },
          ].map((p, i) => (
            <Reveal key={i} delay={i * 100} className="col-span-12 md:col-span-4 border border-[hsl(var(--bb-text))]/12 p-8 bg-[hsl(var(--bb-ivory-soft))]">
              <span className="text-[hsl(var(--bb-gold))] inline-flex items-center justify-center w-12 h-12 border border-[hsl(var(--bb-gold))]/40">{p.icon}</span>
              <div className="bb-display text-2xl md:text-3xl mt-5 text-[hsl(var(--bb-text))]">{p.title}</div>
              <p className="mt-3 text-[hsl(var(--bb-text-muted))] leading-relaxed">{p.body}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* KEYWORD-RICH EDITORIAL COPY */}
      <section className="bg-[hsl(var(--bb-ivory-soft))] border-y border-[hsl(var(--bb-text))]/10 py-20 md:py-28">
        <div className="bb-container grid grid-cols-12 gap-10 items-start">
          <div className="col-span-12 lg:col-span-5">
            <Reveal>
              <div className="bb-eyebrow">Why Bombay Bespoke</div>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="bb-display text-4xl md:text-5xl mt-4 text-[hsl(var(--bb-text))]">
                Dubai’s most trusted
                <span className="bb-italic text-[hsl(var(--bb-gold))]"> bespoke tailor.</span>
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-7 space-y-6 text-[hsl(var(--bb-text-muted))] leading-relaxed text-base md:text-lg">
            <Reveal delay={200}>
              <p>
                Bombay Bespoke Fashion House LLC is a <strong className="text-[hsl(var(--bb-text))]">luxury tailoring company in Al Karama, Dubai</strong>, founded by Mr. Hardeep Singh Gill — widely regarded as the <strong className="text-[hsl(var(--bb-text))]">best suit tailor in Al Karama Dubai</strong>. For three decades, we have provided <strong className="text-[hsl(var(--bb-text))]">bespoke tailoring services in Al Karama Dubai</strong>, dressing executives, grooms, business leaders and everyday gentlemen who value fit, cloth and craftsmanship.
              </p>
            </Reveal>
            <Reveal delay={280}>
              <p>
                Every garment we make is entirely <strong className="text-[hsl(var(--bb-text))]">custom made</strong> — there are no off-the-rack silhouettes here. Whether you are commissioning <strong className="text-[hsl(var(--bb-text))]">tailored business suits in Al Karama Dubai</strong>, planning a wedding with our <strong className="text-[hsl(var(--bb-text))]">wedding suit tailor in Al Karama Dubai</strong>, or looking for a trusted <strong className="text-[hsl(var(--bb-text))]">gents tailor in Al Karama Dubai</strong> for formal wear, we bring the same attention to detail that has earned us a loyal clientele across the UAE.
              </p>
            </Reveal>
            <Reveal delay={360}>
              <p>
                Our <strong className="text-[hsl(var(--bb-text))]">custom suits couturier in Karama Dubai</strong> service covers everything from <strong className="text-[hsl(var(--bb-text))]">handmade suits</strong> and tuxedos to <strong className="text-[hsl(var(--bb-text))]">men’s bespoke suits</strong>, sherwanis, kurtas, shirts and trousers. Our sister brand VM by Vandana provides <strong className="text-[hsl(var(--bb-text))]">personalized tailoring services in Al Karama Dubai</strong> for women — bridal lehengas, couture gowns, saris and made-to-order suits.
              </p>
            </Reveal>
            <Reveal delay={440}>
              <p>
                We are also known for <strong className="text-[hsl(var(--bb-text))]">affordable custom suits</strong> that don’t compromise on cloth quality or fit, and we accept <strong className="text-[hsl(var(--bb-text))]">suit alterations and outfit alteration services in Al Karama Dubai</strong> for existing garments. This end-of-season offer — three bespoke suits for AED 3333 — is our thanks to the community that has trusted us for years.
              </p>
            </Reveal>
            <Reveal delay={520}>
              <div className="pt-4">
                <ClaimBtn label="WhatsApp now to claim" source="sale-copy" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* GOOGLE REVIEWS */}
      <section className="bg-[hsl(var(--bb-ivory))] border-t border-[hsl(var(--bb-text))]/8">
        <GoogleReviews variant="home" />
      </section>

      {/* FAQ */}
      <section className="bg-[hsl(var(--bb-ivory-soft))] border-t border-[hsl(var(--bb-text))]/8">
        <Faq
          eyebrow="Offer · FAQs"
          heading="What to know,"
          italicWord="before you claim."
          items={SALE_FAQS}
          tone="gold"
        />
      </section>

      {/* FINAL CTA */}
      <section className="bg-[hsl(var(--bb-charcoal))] text-[hsl(var(--bb-text))] py-20 md:py-28 bb-grain relative overflow-hidden">
        <div className="relative z-10 bb-container text-center max-w-3xl mx-auto">
          <Reveal>
            <div className="bb-eyebrow text-[hsl(var(--bb-gold))]">Claim the Offer</div>
          </Reveal>
          <Reveal delay={120}>
            <h3 className="bb-display text-4xl md:text-6xl mt-4">
              3 suits. <span className="bb-italic text-[hsl(var(--bb-gold))]">AED 3333.</span>
              <br />
              Message us now.
            </h3>
          </Reveal>
          <Reveal delay={240}>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-5">
              <ClaimBtn big label={`WhatsApp — ${CONTACT.phone}`} source="sale-final" />
              <Link to="/book-appointment" className="bb-link bb-link-reverse text-[hsl(var(--bb-gold))]">
                Or book an appointment →
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Hidden but crawlable — keyword list for AdWords quality & SEO */}
      <div className="sr-only" aria-hidden="true">
        {KEYWORDS.join(' · ')}
      </div>
    </div>
  );
}
